import { useState } from 'react';
import { Button, Dialog, Icon, Portal, Text, useFileUpload } from '@chakra-ui/react';
import { LuUpload } from 'react-icons/lu';
import {
  FileUploadHiddenInput,
  FileUploadList,
  FileUploadRootProvider,
  FileUploadTrigger,
} from '@/components/ui/file-upload';
import { toaster } from '@/components/ui/toaster';
import { uploadReceipt } from '@/lib/api';

/**
 * Self-contained "Reemplazar" button: opens its own dialog with a file
 * picker (same accept list as the initial registration upload) and calls
 * the existing `uploadReceipt` — the server supersedes the prior receipt
 * (retains it) rather than deleting it.
 */
export function ReplaceReceiptButton({ paymentId, onDone }: { paymentId: string; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileUpload = useFileUpload({ maxFiles: 1, accept: ['application/pdf', 'image/jpeg', 'image/png'] });

  async function handleReplace() {
    const file = fileUpload.acceptedFiles[0];
    if (!file) {
      toaster.create({ title: 'Selecciona un archivo', type: 'warning' });
      return;
    }
    setBusy(true);
    try {
      await uploadReceipt(paymentId, file);
      toaster.create({ title: 'Comprobante reemplazado', type: 'success' });
      fileUpload.clearFiles();
      setOpen(false);
      onDone();
    } catch (err) {
      toaster.create({
        title: 'No se pudo reemplazar el comprobante',
        description: err instanceof Error ? err.message : undefined,
        type: 'error',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog.Root
      open={open}
      size="sm"
      // Defensive complement to the VoidPaymentDialog fix (see its comment): this
      // Dialog.Root stays mounted and toggles via `open` (the idiomatic pattern),
      // so it isn't exposed to the same unmount-while-open crash. Still, no
      // Dialog.CloseTrigger is rendered here either, so pin `finalFocusEl` to a
      // node that's never detached rather than relying on zag's default trigger
      // lookup during teardown.
      finalFocusEl={() => document.body}
      onOpenChange={(d) => setOpen(d.open)}
    >
      <Dialog.Trigger asChild>
        <Button size="xs" variant="ghost">
          Reemplazar
        </Button>
      </Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Reemplazar comprobante</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Text fontSize="sm" color="fg.muted" mb="3">
                El comprobante actual se conserva para auditoría; se agrega el nuevo archivo.
              </Text>
              <FileUploadRootProvider value={fileUpload}>
                <FileUploadHiddenInput />
                <FileUploadTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Icon as={LuUpload} /> Seleccionar archivo
                  </Button>
                </FileUploadTrigger>
                <FileUploadList showSize clearable />
              </FileUploadRootProvider>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
                Cancelar
              </Button>
              <Button
                colorPalette="brand"
                bg="brand.600"
                color="white"
                _hover={{ bg: 'brand.700' }}
                loading={busy}
                onClick={handleReplace}
              >
                Reemplazar
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
